/**
 * RulesPage — author, disable and re-enable validation rules.
 *
 * Split out of the Checks Agent tab deliberately. That table answers "what did
 * this session do", and it is read while a capture is in front of you; editing
 * the rule set is a different job, done rarely and never mid-triage. Mixing
 * them put an Add-rule box and a Disable button on every row of a list people
 * were only trying to read.
 *
 * Nothing here duplicates the verdict view: no results, no per-request values.
 * This page is about which rules EXIST, not what they found.
 *
 * It lists the rules that run by default — the built-in set plus the user's
 * own — grouped by category. Storage details (suite names, the compiled
 * query) stay out of the copy: the query is on hover, the id is on hover.
 * Launch-specific rule sets live with their launch under Sign off.
 *
 * "Catch-alls" are the few built-in rules that fail on ANY fail-level
 * validator finding for a request type ("GAM requests carry no fail-level
 * findings"). They are grouped apart because they overlap the specific rules.
 * Rules that read ONE named finding (coppa_id_leak, ortbresp.bid.price.negative,
 * …) also read `issues`, but they are real, specific checks — they stay in
 * their category. Grouping every issues-reading rule as a "roll-up" hid 37 of
 * those, including the COPPA / opt-out / LAT leak checks.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import {
  getSuiteChecks, listQuarantine, quarantineCheck, releaseCheck,
  appendCheckToSuite, compileCheck, paramCoverage,
} from '../api/checks';
import { PROXY_API_BASE } from '../apiBase';
import { ruleExpression, readRows, nameRules } from '../utils/ruleExpression';

const BASELINE = 'adtech-baseline';
const USER_SUITE = 'my-rules';

function errText(e) {
  return (e && (e.body?.detail || e.body?.error || e.message)) || 'Something went wrong';
}

/* The query the rule actually runs, e.g. `?iu= present · on every request`,
 * as one string for the row's hover title. Aggregates have no one-liner, so
 * they fall back to a line per read source. */
// Hover text for a rule row: the plain explanation first (what is inspected,
// why it matters, what to do), then the compiled query for whoever debugs it.
function ruleTooltip(check) {
  const parts = [];
  if (check && check.description && check.description !== check.statement) parts.push(check.description);
  if (check && check.why) parts.push(`Why it matters: ${check.why}`);
  if (check && check.fix) parts.push(`If it fails: ${check.fix}`);
  const q = looksForText(check);
  if (q) parts.push(`Looks for: ${q}`);
  return parts.join('\n\n');
}

function looksForText(check) {
  const one = ruleExpression(check);
  if (one) return one;
  return readRows(check).map((r) => `${r.label}: ${r.value}`).join('\n');
}

// `^(required|fail|error)\|` — a severity filter with no finding code after it.
const SEVERITY_ONLY = /^\^\((required|fail|error)(\|(required|fail|error))*\)\\\|$/;

/**
 * Catch-all: fails on ANY fail-level validator finding, naming no finding code —
 * no param/pattern at all, a bare severity param (`fail|`), or a severity-only
 * pattern. A rule naming a code (`coppa_id_leak`) is specific, not a catch-all.
 */
export function isCatchAll(check) {
  const a = (check && check.assert) || {};
  if (a.source !== 'issues') return false;
  const param = a.param == null ? '' : String(a.param);
  const pattern = a.pattern == null ? '' : String(a.pattern);
  if (!param && !pattern) return true;
  if (param && /^(required|fail|error)\|?$/.test(param)) return true;
  return !!pattern && SEVERITY_ONLY.test(pattern);
}

const ROLLUP_GROUP = 'Catch-alls';
const USER_GROUP = 'Your rules';

/**
 * groupRules(checks) -> [{ name, checks }] — your rules first, then each
 * category by size, roll-ups last. Pure, so the grouping is testable.
 */
export function groupRules(checks) {
  const by = new Map();
  for (const c of checks || []) {
    const g = c.__mine ? USER_GROUP : isCatchAll(c) ? ROLLUP_GROUP : (c.category || 'Other');
    if (!by.has(g)) by.set(g, []);
    by.get(g).push(c);
  }
  const rank = (name) => (name === USER_GROUP ? -1 : name === ROLLUP_GROUP ? 1 : 0);
  return [...by.entries()]
    .map(([name, list]) => ({ name, checks: list }))
    .sort((a, b) => rank(a.name) - rank(b.name) || b.checks.length - a.checks.length || a.name.localeCompare(b.name));
}

/* ── Undefined parameters ────────────────────────────────────────────────
 * The other half of coverage. The rules above can only judge a parameter
 * someone wrote a rule about, and the knowledge packs can only describe one
 * someone documented — both hand-maintained, both drifting behind the SDKs.
 * This reads the keys off the CAPTURED requests and shows what nothing
 * describes.
 *
 * Ranked by presence, because a GAM URL carries ~55 query params and most are
 * Google-internal telemetry. A key on every request is structural and worth
 * defining; one on a handful is a one-off. The top of the list is the
 * answerable question.
 */
function CoveragePanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [onlyCommon, setOnlyCommon] = useState(true);

  const run = useCallback(() => {
    setBusy(true); setErr(null);
    paramCoverage({ minPresence: onlyCommon ? 0.5 : 0 })
      .then(setData)
      .catch((e) => setErr(errText(e)))
      .finally(() => setBusy(false));
  }, [onlyCommon]);

  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'var(--muted)', display: 'block', mb: 1 }}>
        Parameters your captured requests carry that the tool has no description for, so no
        rule can check their values yet. Free: reads the session, calls no model.
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Button size="small" variant="outlined" onClick={run} disabled={busy}>
          {busy ? <CircularProgress size={16} /> : 'Scan captured traffic'}
        </Button>
        <FormControlLabel
          control={<Checkbox size="small" checked={onlyCommon} onChange={(e) => setOnlyCommon(e.target.checked)} />}
          label={<Typography variant="caption">Only parameters on most requests</Typography>}
        />
        {data && (
          <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
            {data.totals.defined} described · {data.totals.undefined} not described
          </Typography>
        )}
      </Stack>
      {err && <Alert severity="error">{err}</Alert>}
      {data && !data.types.length && (
        <Typography variant="body2" sx={{ color: 'var(--muted)' }}>
          No captured traffic in scope.
        </Typography>
      )}
      {data && data.types.map((t) => (
        <Box key={t.requestType} sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.requestType}</Typography>
            <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
              {t.rowCount} request(s) · {t.defined.length} described · {t.undefined.length} not described
            </Typography>
          </Stack>
          {t.undefined.length ? (
            <Typography
              variant="body2"
              sx={{ fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace', overflowWrap: 'anywhere', color: 'warning.main' }}
            >
              {t.undefined.map((d) => d.param).join(', ')}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: 'success.main' }}>every parameter is described</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

/* Promotion data (proxy-server/conformance/promotion.js, GET /proxy/checks/promotions):
 * { window, apps: { [app]: { enforcing:[id], streaks:{id:{streak,runs}}, proposals:[{checkId,runs}] } } }.
 * A failed read is null — rows then just show their plain severity. */
async function loadPromotions() {
  try {
    const res = await fetch(`${PROXY_API_BASE}/proxy/checks/promotions`, { headers: { Accept: 'application/json' } });
    if (!res || !res.ok) return null;
    return await res.json();
  } catch { return null; }
}

/**
 * The enforcement state of one rule:
 *   quarantined | enforcing | proposed | warn | info, plus the run count behind it.
 * Quarantine wins (it softens whatever else is true); `enforcing` is fail severity OR
 * promoted via an App Pack's policy.enforce; `proposed` = stable pass for the whole
 * window in some app but not yet enforced (accepting is a pack edit, never automatic).
 */
export function ruleStatus(check, { quarantined, promo } = {}) {
  const id = check && check.id;
  const win = (promo && Number(promo.window)) || 10;
  const apps = (promo && promo.apps && typeof promo.apps === 'object') ? Object.entries(promo.apps) : [];
  if (quarantined && quarantined.has(id)) return { state: 'quarantined', detail: 'flapping — verdict softened to warn' };
  const promotedIn = apps.filter(([, a]) => a && Array.isArray(a.enforcing) && a.enforcing.includes(id)).map(([n]) => n);
  if (promotedIn.length) return { state: 'enforcing', detail: `promoted for ${promotedIn.join(', ')}` };
  if (check && check.severity === 'fail') return { state: 'enforcing', detail: 'fail severity' };
  let best = null;
  for (const [app, a] of apps) {
    const prop = a && Array.isArray(a.proposals) ? a.proposals.find((p) => p && p.checkId === id) : null;
    if (prop) return { state: 'proposed', detail: `${prop.runs}/${win} clean runs on ${app} — add to policy.enforce to promote` };
    const st = a && a.streaks && a.streaks[id];
    if (st && (!best || st.streak > best.streak)) best = { ...st, app };
  }
  if (check && check.severity === 'warn') {
    return { state: 'warn', detail: best ? `${best.streak}/${win} clean runs${best.app ? ` on ${best.app}` : ''}` : 'no comparable runs yet' };
  }
  return { state: 'info', detail: '' };
}

const STATE_COLOR = {
  quarantined: 'var(--muted)', enforcing: 'var(--error-text)', proposed: 'var(--info-text, var(--warn-text))',
  warn: 'var(--warn-text)', info: 'var(--muted)',
};

export default function RulesPage({ embedded = false } = {}) {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [filter, setFilter] = useState('');
  const [show, setShow] = useState('all'); // 'all' | 'builtin' | 'mine'

  const [quarantined, setQuarantined] = useState(new Set());
  const [pending, setPending] = useState(null);
  const [promo, setPromo] = useState(null);

  const [draft, setDraft] = useState('');
  const [authoring, setAuthoring] = useState(false);
  const [confirmAuthor, setConfirmAuthor] = useState(false);

  const refreshQuarantine = useCallback(() => {
    listQuarantine()
      // listQuarantine() resolves the array itself; tolerate the raw { quarantined } shape too.
      .then((r) => setQuarantined(new Set((Array.isArray(r) ? r : (r?.quarantined || [])).map((q) => q.checkId))))
      .catch(() => { /* a failed read just shows nothing disabled */ });
  }, []);

  // Built-in + yours, tagged so a row knows which suite it lives in (Disable
  // needs it). No user rules yet is the normal case, not an error.
  const loadChecks = useCallback(() => {
    setLoading(true); setError(null);
    const tag = (suite, mine) => (r) => (Array.isArray(r?.checks) ? r.checks : [])
      .map((c) => ({ ...c, __suite: suite, __mine: mine }));
    Promise.all([
      getSuiteChecks(BASELINE).then(tag(BASELINE, false)),
      getSuiteChecks(USER_SUITE).then(tag(USER_SUITE, true)).catch(() => []),
    ])
      .then(([builtin, mine]) => setChecks([...mine, ...builtin]))
      .catch((e) => setError(errText(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadChecks(); refreshQuarantine(); loadPromotions().then(setPromo); }, [loadChecks, refreshQuarantine]);

  const toggle = useCallback(async (check, isOff) => {
    setPending(check.id); setNote(null);
    try {
      if (isOff) await releaseCheck(check.id);
      else await quarantineCheck(check.id, { suite: check.__suite });
      refreshQuarantine();
    } catch (e) { setNote(errText(e)); } finally { setPending(null); }
  }, [refreshQuarantine]);

  const addRule = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;
    setAuthoring(true); setNote(null);
    try {
      const compiled = await compileCheck(text);
      const out = (compiled && compiled.checks)
        || (compiled && compiled.check ? [compiled.check] : []);
      if (!out.length) {
        setNote((compiled && (compiled.issues || []).join('; ')) || 'That did not turn into a rule. Try naming the parameter and the request type.');
        return;
      }
      for (const c of out) await appendCheckToSuite(USER_SUITE, c);
      setNote(`Added ${out.length} rule${out.length === 1 ? '' : 's'} — see “${USER_GROUP}” below.`);
      setDraft(''); setConfirmAuthor(false);
      loadChecks();
    } catch (e) { setNote(errText(e)); } finally { setAuthoring(false); }
  }, [draft, loadChecks]);

  // Names are derived from the whole set, because collisions can only be seen
  // across it — computed once here rather than per row.
  const names = useMemo(() => nameRules(checks), [checks]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return checks.filter((c) => {
      if (show === 'builtin' && c.__mine) return false;
      if (show === 'mine' && !c.__mine) return false;
      if (!q) return true;
      const types = c.target && c.target.requestType;
      // The NAME must be in the haystack: it is the parameter key, so typing
      // `sz` has to find the sz rules even when the id spells it differently.
      return [names.get(c.id), c.id, c.statement, c.description, c.category,
        ruleExpression(c), Array.isArray(types) ? types.join(' ') : types]
        .filter(Boolean).join(' ').toLowerCase()
        .includes(q);
    });
  }, [checks, filter, show, names]);

  const groups = useMemo(() => groupRules(visible), [visible]);
  const mineCount = useMemo(() => checks.filter((c) => c.__mine).length, [checks]);
  const offCount = useMemo(() => checks.filter((c) => quarantined.has(c.id)).length, [checks, quarantined]);
  const filtering = filter.trim().length > 0;

  return (
    // `embedded`: rendered as the Rules mode inside ChecksPage, which owns the
    // page padding and title. The standalone /rules route redirects there.
    <Box sx={embedded ? undefined : { p: 3, maxWidth: 1200, mx: 'auto' }}>
      {!embedded && <Typography variant="h5" sx={{ mb: 0.5 }}>Rules</Typography>}
      <Typography variant="body2" sx={{ color: 'var(--muted)', mb: 2 }}>
        Every rule the checks run, and a switch to turn any of them off. Results are on the
        Agent tab.
      </Typography>

      {note && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNote(null)}>{note}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Add a rule</Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth size="small"
            placeholder="e.g. “internal_tag must be present on every GAM request”"
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setConfirmAuthor(false); }}
            disabled={authoring}
          />
          <Button
            variant="contained" disabled={authoring || !draft.trim()}
            onClick={() => (confirmAuthor ? addRule() : setConfirmAuthor(true))}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {authoring ? <CircularProgress size={18} /> : confirmAuthor ? 'Confirm — uses AI' : 'Add rule'}
          </Button>
        </Stack>
        <Typography variant="caption" sx={{ color: 'var(--muted)', display: 'block', mt: 1 }}>
          Write it in plain English. It runs from the next check onward, alongside the built-in rules.
        </Typography>
      </Paper>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          size="small" sx={{ flex: 1, minWidth: 220 }} value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder={`Search ${checks.length} rules — a parameter, vendor or word`}
        />
        <ToggleButtonGroup size="small" exclusive value={show} onChange={(e, v) => { if (v) setShow(v); }}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="builtin">Built-in</ToggleButton>
          <ToggleButton value="mine">Yours ({mineCount})</ToggleButton>
        </ToggleButtonGroup>
        {offCount > 0 && <Chip size="small" color="warning" variant="outlined" label={`${offCount} turned off`} />}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {loading ? <CircularProgress size={22} /> : (
        <>
          {!groups.length && (
            <Typography variant="body2" sx={{ color: 'var(--muted)', py: 2 }}>
              {filtering ? `No rule matches “${filter}”.`
                : show === 'mine' ? 'You have not added any rules yet.' : 'No rules loaded.'}
            </Typography>
          )}
          {groups.map((g) => {
            const off = g.checks.filter((c) => quarantined.has(c.id)).length;
            const rollup = g.name === ROLLUP_GROUP;
            return (
              <Accordion
                key={g.name} variant="outlined" disableGutters
                // While searching, every matching group is open so hits are visible.
                {...(filtering ? { expanded: true } : { defaultExpanded: g.name === USER_GROUP })}
                sx={{ '&:before': { display: 'none' }, mb: 1 }}
              >
                <AccordionSummary>
                  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ width: '100%' }}>
                    <Typography variant="subtitle2">{g.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
                      {g.checks.length} rule{g.checks.length === 1 ? '' : 's'}{off ? ` · ${off} off` : ''}
                    </Typography>
                    {rollup && (
                      <Typography variant="caption" sx={{ color: 'var(--muted)', ml: 'auto !important' }}>
                        fail on any fail-level validator finding — overlap the specific rules above
                      </Typography>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableBody>
                      {g.checks.map((c) => {
                        const isOff = quarantined.has(c.id);
                        return (
                          <TableRow key={c.id} hover sx={isOff ? { opacity: 0.5 } : undefined}>
                            {/* The parameter key, not the file handle. The full id
                                stays on hover — quarantine, tickets and launch
                                criteria key on it, so it must remain referable. */}
                            <TableCell
                              title={c.id}
                              sx={{ width: '22%', fontFamily: 'var(--font-mono)', fontSize: 12, overflowWrap: 'anywhere' }}
                            >
                              {names.get(c.id) || c.id}
                            </TableCell>
                            {/* The compiled query is for whoever debugs a rule,
                                not for reading the list — it is the hover. */}
                            <TableCell sx={{ fontSize: 12.5, wordBreak: 'break-word' }} title={ruleTooltip(c) || undefined}>
                              {c.statement || c.description || '—'}
                            </TableCell>
                            {(() => {
                              const st = ruleStatus(c, { quarantined, promo });
                              return (
                                <TableCell
                                  sx={{ width: 150, fontSize: 11, color: STATE_COLOR[st.state] }}
                                  title={st.detail || undefined}
                                  data-testid={`rule-state-${c.id}`}
                                >
                                  {st.state}
                                  {st.detail && (
                                    <Box component="span" sx={{ display: 'block', color: 'var(--muted)', fontSize: 10.5 }}>
                                      {st.detail}
                                    </Box>
                                  )}
                                </TableCell>
                              );
                            })()}
                            <TableCell align="right" sx={{ width: 84 }}>
                              <Button
                                size="small" disabled={pending === c.id}
                                sx={{ fontSize: 11, minWidth: 0, whiteSpace: 'nowrap' }}
                                onClick={() => toggle(c, isOff)}
                              >
                                {pending === c.id ? <CircularProgress size={13} /> : isOff ? 'Turn on' : 'Turn off'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      )}

      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, mb: 3, color: 'var(--muted)' }}>
        Turning a rule off only softens its verdict from the next run; nothing is deleted, and
        it can be turned back on here.
      </Typography>

      <Accordion variant="outlined" disableGutters sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary>
          <Typography variant="subtitle2">Parameters no rule can check yet</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CoveragePanel />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
